export type CommunicationSource = 'aac' | 'voice' | 'gesture';

export type CommunicationEvent = {
  id: string;
  phrase: string;
  intent: string;
  confidence: number;
  source: CommunicationSource;
  locale: string;
  createdAt: number;
};

export type CommunicationEventInput = Omit<CommunicationEvent, 'id' | 'createdAt'> & {
  id?: string;
  createdAt?: number;
};

export const MAX_COMMUNICATION_HISTORY_EVENTS = 50;

const BASE_STORAGE_KEY = 'ecovoz:communication-history';

let storageKey = BASE_STORAGE_KEY;

const normalizeText = (value: string): string => value.trim().replace(/\s+/g, ' ');

function isValidEvent(value: unknown): value is CommunicationEvent {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.id === 'string' &&
    typeof obj.phrase === 'string' &&
    typeof obj.intent === 'string' &&
    typeof obj.confidence === 'number' &&
    (obj.source === 'aac' || obj.source === 'voice' || obj.source === 'gesture') &&
    typeof obj.locale === 'string' &&
    typeof obj.createdAt === 'number'
  );
}

function loadFromStorage(): CommunicationEvent[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEvent);
  } catch {
    return [];
  }
}

function saveToStorage(events: CommunicationEvent[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(events));
  } catch {
    // quota exceeded or localStorage unavailable — fail silently
  }
}

const history: CommunicationEvent[] = loadFromStorage();

/**
 * Switches the storage key to a user-specific namespace and reloads history
 * from that key. Call this after a successful login.
 */
export function setHistoryOwner(userId: string): void {
  storageKey = `${BASE_STORAGE_KEY}:${userId}`;
  const loaded = loadFromStorage();
  history.length = 0;
  for (const event of loaded) {
    history.push(event);
  }
}

/**
 * Clears the user-specific history from storage and memory, then reverts the
 * storage key to the anonymous default. Call this on logout.
 */
export function clearHistoryOwner(): void {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // unavailable — fail silently
  }
  history.length = 0;
  storageKey = BASE_STORAGE_KEY;
}

export const createCommunicationEventId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `evt-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

export function getCommunicationHistoryScopeKey(): string {
  return storageKey;
}

export function recordCommunicationEvent(input: CommunicationEventInput): CommunicationEvent {
  const event: CommunicationEvent = {
    id: input.id ?? createCommunicationEventId(),
    phrase: normalizeText(input.phrase),
    intent: normalizeText(input.intent),
    confidence: input.confidence,
    source: input.source,
    locale: normalizeText(input.locale || 'pt'),
    createdAt: input.createdAt ?? Date.now(),
  };

  history.unshift(event);

  if (history.length > MAX_COMMUNICATION_HISTORY_EVENTS) {
    history.length = MAX_COMMUNICATION_HISTORY_EVENTS;
  }

  saveToStorage(history);
  return event;
}

export function deleteCommunicationEvent(id: string): void {
  const index = history.findIndex((event) => event.id === id);
  if (index !== -1) {
    history.splice(index, 1);
    saveToStorage(history);
  }
}

export function getCommunicationHistory(): CommunicationEvent[] {
  return [...history];
}

export function clearCommunicationHistory(): void {
  history.length = 0;
  saveToStorage([]);
}
